import React, { useCallback, useEffect, useRef, useState } from "react";
import { EVENT_NAMES } from "@scrile/api-provider/dist";
import { getAuthToken } from "lib/providers";
import providers from "lib/providers";
import emitter from "lib/emitter";
import { fetchWithMediaCache } from "lib/mediaCache";
import { AiFeatureDisabledError } from "lib/aiApi";
import { useTranslation } from "locales";
import {
  buildTranslationAudioUrl,
  fetchVoiceTranslation,
  LANGUAGE_LABELS,
  translateVoiceMessage,
  VoiceTranslation,
} from "lib/voiceCloneApi";
import "./style.scss";

interface Props {
  src: string;
  duration?: number;
  isOwn?: boolean;
  messageId?: string;
  existingTranscription?: string;
  /**
   * ID of the user whose voice gets cloned for translations. The
   * graphql resolver no longer fetches the message from the Java
   * backend, so the bubble must hand over the sender id at click time.
   */
  senderUserId?: string;
}

const NUM_BARS = 32;

// Message ids whose transcription the user has explicitly collapsed. Module-
// level so the choice survives Virtuoso recycling this row (remount) — without
// it a dismissed transcription re-opened every time the row scrolled back into
// view or a persisted transcription arrived on a later list merge.
const dismissedTranscriptions = new Set<string>();

/**
 * Wait for the SDK's `SET_USER` event with a hard timeout. Used to
 * recover from the page-reload race where graphql-server replies
 * `Authentication required` because the click hit before the WebSocket
 * handshake finished populating the request context. Resolves true if
 * the event fires in time, false on timeout.
 */
function waitForAuth(timeoutMs = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const onSet = () => {
      if (done) return;
      done = true;
      emitter.off(EVENT_NAMES.SET_USER, onSet);
      resolve(true);
    };
    emitter.on(EVENT_NAMES.SET_USER, onSet);
    window.setTimeout(() => {
      if (done) return;
      done = true;
      emitter.off(EVENT_NAMES.SET_USER, onSet);
      resolve(false);
    }, timeoutMs);
  });
}

function isAuthError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e || "");
  return /Authentication required|UNAUTHENTICATED|HTTP 401/i.test(msg);
}

function generateWaveform(): number[] {
  const bars: number[] = [];
  let prev = 0.4;
  for (let i = 0; i < NUM_BARS; i++) {
    const positionFactor = 1 - Math.abs(i / NUM_BARS - 0.5) * 0.6;
    const drift = (Math.random() - 0.5) * 0.5;
    prev = Math.max(0.1, Math.min(1, prev + drift));
    bars.push(prev * positionFactor);
  }
  return bars;
}

function VoiceMessagePlayer({ src, duration: initialDuration, isOwn, messageId, existingTranscription, senderUserId }: Props) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const waveform = React.useMemo(() => generateWaveform(), []);

  // Transcription state. Auto-open a pre-existing transcription on first view,
  // but respect a prior user dismissal (remember-across-remount, see the
  // module-level set above).
  const [transcription, setTranscription] = useState<string | null>(existingTranscription || null);
  const [showTranscription, setShowTranscription] = useState(
    !!existingTranscription && !(messageId && dismissedTranscriptions.has(messageId))
  );
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState(false);

  // Translation state — only relevant when feature is enabled and the
  // message already has a transcription (LLM needs source text).
  const [translationLang, setTranslationLang] = useState<string>("original");
  const [translation, setTranslation] = useState<VoiceTranslation | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [translationDisabled, setTranslationDisabled] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Auth-fetched blob URL for the translated MP3. The router protects
  // /voice-translations/files/:name with requireAuth, so the browser's
  // raw <audio src="…"> request gets a 401 — we have to fetch with our
  // bearer header (same trick as the original-audio blob above).
  const [translationBlobUrl, setTranslationBlobUrl] = useState<string | null>(null);

  const TRANSLATE_LANGS = ["en", "es", "de", "fr", "ru"];

  // Sync when existingTranscription prop lands via a later list merge. Only
  // SEED the text — do NOT force the panel open (that re-appeared a transcript
  // the user had collapsed). Auto-open only if never dismissed.
  useEffect(() => {
    if (existingTranscription && !transcription) {
      setTranscription(existingTranscription);
      if (!(messageId && dismissedTranscriptions.has(messageId))) {
        setShowTranscription(true);
      }
    }
  }, [existingTranscription]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active audio source: original blob, or the translated MP3 once it's
  // both done AND fetched-with-auth into a blob URL.
  const activeAudioSrc = (translation && translation.status === "done" && translationBlobUrl)
    ? translationBlobUrl
    : blobUrl;

  // Fetch the translated MP3 with auth headers when it lands.
  useEffect(() => {
    if (!(translation && translation.status === "done" && translation.audioUrl)) {
      // Switching back to "original" or to a different pending language
      // — drop any previously fetched translation blob.
      setTranslationBlobUrl(null);
      return;
    }
    const url = buildTranslationAudioUrl(translation.audioUrl);
    if (!url) return;
    let cancelled = false;
    let createdBlobUrl: string | null = null;

    // Same race we hit in handleTranslate: on a fresh page load
    // getAuthToken() can return empty before the websocket handshake
    // has populated the token, so the first fetch goes without
    // Authorization → router replies 401. Retry once after SET_USER.
    const fetchWithAuthRetry = async (): Promise<Response> => {
      const tryFetch = async () => {
        const token = await getAuthToken();
        return fetch(url, {
          credentials: "include",
          headers: token ? { Authorization: token } : {},
        });
      };
      const first = await tryFetch();
      if (first.status !== 401) return first;
      const ready = await waitForAuth();
      if (!ready) return first;
      return tryFetch();
    };

    (async () => {
      try {
        const resp = await fetchWithAuthRetry();
        if (cancelled) return;
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        createdBlobUrl = URL.createObjectURL(blob);
        setTranslationBlobUrl(createdBlobUrl);
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load translated voice:", e);
          setTranslationError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
      if (createdBlobUrl) {
        setTimeout(() => URL.revokeObjectURL(createdBlobUrl!), 3000);
      }
    };
  }, [translation && translation.status, translation && translation.audioUrl]);

  // Poll an in-progress translation until it transitions to done|failed.
  useEffect(() => {
    if (!messageId) return;
    if (!translation || translation.status !== "pending") return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const next = await fetchVoiceTranslation(messageId, translation.targetLanguage);
        if (stopped || !next) return;
        setTranslation(next);
        if (next.status === "pending") {
          window.setTimeout(tick, 4000);
        } else if (next.status === "failed") {
          setTranslationError(next.error || t("Translation failed"));
        }
      } catch (e) {
        if (!stopped) {
          setTranslationError(e instanceof Error ? e.message : String(e));
        }
      }
    };
    const handle = window.setTimeout(tick, 4000);
    return () => { stopped = true; window.clearTimeout(handle); };
  }, [translation, messageId]);

  const handleTranslate = useCallback(async (lang: string) => {
    setPickerOpen(false);
    setTranslationError(null);
    if (lang === "original") {
      setTranslationLang("original");
      setTranslation(null);
      return;
    }
    if (!messageId) return;
    setTranslationLang(lang);
    setTranslating(true);
    try {
      // Both calls go through getAuthToken() — on a fresh page load the
      // websocket handshake may not have populated auth state yet, so
      // we transparently retry once after waiting for SET_USER.
      const callWithAuthRetry = async <T,>(fn: () => Promise<T>): Promise<T> => {
        try { return await fn(); }
        catch (err) {
          if (!isAuthError(err)) throw err;
          const ready = await waitForAuth();
          if (!ready) throw err;
          return fn();
        }
      };

      const cached = await callWithAuthRetry(() => fetchVoiceTranslation(messageId, lang));
      if (cached && cached.status === "done") {
        setTranslation(cached);
      } else {
        const sourceText = (transcription || existingTranscription || "").trim();
        if (!sourceText) {
          throw new Error("Transcribe the voice message first");
        }
        if (!senderUserId) {
          throw new Error("Voice message has no sender");
        }
        // Pass the source clip duration so F5-TTS can match the
        // translation length to the original (otherwise it falls back
        // to its own under-budgeted estimate).
        const sourceDurationSec = duration && Number.isFinite(duration) && duration > 0
          ? duration
          : undefined;
        const result = await callWithAuthRetry(() => translateVoiceMessage(
          messageId, lang, sourceText, senderUserId, sourceDurationSec,
        ));
        setTranslation(result);
      }
    } catch (e) {
      if (e instanceof AiFeatureDisabledError) {
        setTranslationDisabled(true);
      } else {
        setTranslationError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setTranslating(false);
    }
  }, [messageId, transcription, existingTranscription, senderUserId, duration]);

  // Fetch audio with auth headers and create blob URL (cached via Cache API)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled) return;
        const url = await fetchWithMediaCache(src, token ? { Authorization: token } : {}, "audio/webm");
        if (!cancelled) setBlobUrl(url);
      } catch (e) {
        console.error("Failed to load voice message:", e);
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src]);

  // Cleanup blob URL on unmount (delayed to let browser finish pending loads)
  useEffect(() => {
    return () => {
      if (blobUrl) setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    };
  }, [blobUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
  }, [blobUrl]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    }
  }, [playing]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setCurrentTime(audio.currentTime);
  }, [duration]);

  const cyclePlaybackRate = useCallback(() => {
    const rates = [1, 1.5, 2];
    const nextIdx = (rates.indexOf(playbackRate) + 1) % rates.length;
    const newRate = rates[nextIdx];
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  }, [playbackRate]);

  const handleTranscribe = useCallback(async () => {
    if (transcription !== null) {
      setShowTranscription((v) => {
        const next = !v;
        // Remember an explicit collapse so it isn't re-opened on remount / a
        // later existingTranscription sync.
        if (messageId) {
          if (next) dismissedTranscriptions.delete(messageId);
          else dismissedTranscriptions.add(messageId);
        }
        return next;
      });
      return;
    }
    if (!messageId || transcribing) return;

    setShowTranscription(true);
    setTranscribing(true);
    setTranscribeError(false);

    try {
      const result = await (providers as any).MessagesProvider.transcribeVoiceMessage({ messageId });
      const details = result?.details;
      const text = details?.voiceTranscription || "";
      setTranscription(text);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Transcription failed:", e);
      setTranscribeError(true);
      setShowTranscription(false);
    } finally {
      setTranscribing(false);
    }
  }, [transcription, messageId, transcribing]);

  const progress = duration > 0 ? currentTime / duration : 0;

  const formatTime = (secs: number) => {
    const s = Math.round(secs);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const displayTime = playing ? formatTime(currentTime) : formatTime(duration || 0);

  if (loadError) {
    return <div className="voice-player __error">{t("Voice message unavailable")}</div>;
  }

  return (
    <>
      <div className={`voice-player${isOwn ? " __own" : ""}`}>
        {activeAudioSrc && <audio ref={audioRef} src={activeAudioSrc} preload="metadata" />}
        <button
          className={`voice-player__play-btn${playing ? " __playing" : ""}`}
          onClick={togglePlay}
          type="button"
          disabled={!blobUrl}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" fill="currentColor" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M8 5v14l11-7z" fill="currentColor" />
            </svg>
          )}
        </button>
        <div className="voice-player__body">
          <div className="voice-player__waveform" onClick={handleSeek}>
            {waveform.map((h, i) => {
              const barProgress = i / NUM_BARS;
              const isPlayed = barProgress < progress;
              return (
                <div
                  key={i}
                  className={`voice-player__bar${isPlayed ? " __played" : ""}`}
                  style={{ height: `${Math.max(8, Math.round(h * 100))}%` }}
                />
              );
            })}
          </div>
          <span className="voice-player__time">{displayTime}</span>
        </div>
        {blobUrl && (
          <button
            className="voice-player__speed-btn"
            onClick={cyclePlaybackRate}
            type="button"
            title={t("Playback speed: {{rate}}x", { rate: playbackRate })}
          >
            {playbackRate}x
          </button>
        )}
        {messageId && (
          <button
            className={`voice-player__transcribe-btn${transcription !== null ? " __done" : ""}${transcribing ? " __loading" : ""}`}
            onClick={handleTranscribe}
            type="button"
            title={transcription !== null ? (showTranscription ? t("Hide transcription") : t("Show transcription")) : t("Transcribe")}
            disabled={transcribing}
          >
            {transcribing ? (
              <span className="voice-player__transcribe-spinner" />
            ) : (
              "Aa"
            )}
          </button>
        )}
        {messageId && transcription && !translationDisabled && (
          <div className="voice-player__translate">
            <button
              type="button"
              className={`voice-player__translate-btn${translationLang !== "original" ? " __active" : ""}${translating ? " __loading" : ""}`}
              onClick={() => setPickerOpen((v) => !v)}
              title={t("Translate to my language (in the speaker's voice)")}
              disabled={translating}
            >
              {translating
                ? "…"
                : translationLang !== "original"
                ? translationLang.toUpperCase()
                : "🌐"}
            </button>
            {pickerOpen && (
              <div className="voice-player__translate-menu" role="menu">
                <button
                  type="button"
                  className={`voice-player__translate-item${translationLang === "original" ? " __active" : ""}`}
                  onClick={() => handleTranslate("original")}
                >
                  {t("Original")}
                </button>
                {TRANSLATE_LANGS.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={`voice-player__translate-item${translationLang === code ? " __active" : ""}`}
                    onClick={() => handleTranslate(code)}
                  >
                    {LANGUAGE_LABELS[code] || code.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showTranscription && (
        <div className={`voice-player__transcription${isOwn ? " __own" : ""}`}>
          {transcribing && <span className="voice-player__transcription-loading">{t("Transcribing...")}</span>}
          {!transcribing && transcription && <p className="voice-player__transcription-text">{transcription}</p>}
          {!transcribing && transcription === "" && <p className="voice-player__transcription-empty">{t("No speech detected")}</p>}
          {transcribeError && <p className="voice-player__transcription-error">{t("Transcription failed. Try again.")}</p>}
          {translation && translation.status === "pending" && (
            <span className="voice-player__transcription-loading">
              {t("Translating to {{lang}}…", { lang: LANGUAGE_LABELS[translation.targetLanguage] || translation.targetLanguage })}
            </span>
          )}
          {translation && translation.status === "done" && translation.transcriptTranslated && (
            <p className="voice-player__transcription-text voice-player__transcription-text--translated">
              <span className="voice-player__translation-tag">
                {LANGUAGE_LABELS[translation.targetLanguage] || translation.targetLanguage.toUpperCase()}
              </span>
              {translation.transcriptTranslated}
            </p>
          )}
          {translationError && (
            <p className="voice-player__transcription-error">
              {/voice profile not found|enroll first|HTTP 404/i.test(translationError)
                ? t("No voice clone is set up yet — record a voice sample in Settings to translate voice messages.")
                : t("Translation failed: {{error}}", { error: translationError })}
            </p>
          )}
        </div>
      )}
    </>
  );
}

export default VoiceMessagePlayer;
